import React, { useState } from 'react';
import { Users, Package, Eye, EyeOff, Scissors, CircleDot, Calculator, ClipboardList, Phone, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../App';
import { useToast } from '../hooks/use-toast';
import InstallAppGuide from './InstallAppGuide';
import appLogo from '../assets/logo.png';

const LoginPage = () => {
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const { login } = useAuth();
  const { toast } = useToast();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    console.log('[LOGIN] Manual login attempt:', { mobile, password: '***' });
    console.log('[LOGIN] Mobile length:', mobile.length, 'Mobile value:', mobile);

    try {
      const result = await login({ mobile, password });
      console.log('[LOGIN] Success:', result);
      toast({
        title: "Success",
        description: "Logged in successfully",
      });
    } catch (error) {
      console.error('[LOGIN] Error:', error);
      console.error('[LOGIN] Error response:', error.response);
      console.error('[LOGIN] Error data:', error.response?.data);
      toast({
        title: "Error", 
        description: error.response?.data?.detail || "Invalid credentials",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (userMobile, userPassword = 'demo123') => {
    console.log('[QUICK LOGIN] Starting quick login for:', userMobile);
    setMobile(userMobile);
    setPassword(userPassword);
    setLoading(true);
    
    try {
      console.log('[QUICK LOGIN] Calling login with:', { mobile: userMobile, password: '***' });
      const result = await login({ mobile: userMobile, password: userPassword });
      console.log('[QUICK LOGIN] Success:', result);
      toast({
        title: "Success",
        description: "Logged in successfully",
      });
    } catch (error) {
      console.error('[QUICK LOGIN] Error:', error);
      console.error('[QUICK LOGIN] Error response:', error.response);
      console.error('[QUICK LOGIN] Error status:', error.response?.status);
      console.error('[QUICK LOGIN] Error data:', error.response?.data);
      console.error('[QUICK LOGIN] Error message:', error.message);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Login failed",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const demoAccounts = [
    { mobile: '9876543210', label: 'Owner', badge: 'Owner', color: 'bg-indigo-100 text-indigo-800' },
    { mobile: '9876543200', label: 'Admin', badge: 'Admin', color: 'bg-red-100 text-red-800' },
    { mobile: '9876543211', label: 'Coordinator', badge: 'Coordinator', color: 'bg-blue-100 text-blue-800' },
    { mobile: '9876543213', label: 'Supervisor', badge: 'Supervisor', color: 'bg-purple-100 text-purple-800' },
    { mobile: '9876543212', label: 'Client', badge: 'Client', color: 'bg-emerald-100 text-emerald-800' },
    { mobile: '9876543214', label: 'Pressing Operator', badge: 'Pressing', color: 'bg-orange-100 text-orange-800' },
    { mobile: '9876543215', label: 'Cutting Operator', badge: 'Cutting', color: 'bg-cyan-100 text-cyan-800' },
    { mobile: '9876543216', label: 'Boring Operator', badge: 'Boring', color: 'bg-pink-100 text-pink-800' },
    { mobile: '9876543217', label: 'Accountant', badge: 'Accounts', color: 'bg-green-100 text-green-800' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        
        {/* Left Side - Branding & Features */}
        <div className="space-y-8 text-center lg:text-left">
          <div className="space-y-4">
            <div className="flex items-center justify-center lg:justify-start space-x-3">
              <div className="w-16 h-16 rounded-xl flex items-center justify-center overflow-hidden">
                <img src={appLogo} alt="Interior Job Work" className="w-16 h-16 object-cover" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Interior Job Work</h1>
                <p className="text-sm text-gray-600">Furniture Production Management</p>
              </div>
            </div>
            <p className="text-xl text-gray-700 leading-relaxed">
              Digitize your furniture factory workflow with role-based dashboards, 
              job card tracking, operator logging, and invoice generation.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <ClipboardList className="w-8 h-8 text-blue-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Job Card Management</h3>
              <p className="text-sm text-gray-600">Create & track pressing, cutting, boring work</p>
            </div>
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <Scissors className="w-8 h-8 text-orange-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Operator Logging</h3>
              <p className="text-sm text-gray-600">Mobile-friendly production entry</p>
            </div>
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <Users className="w-8 h-8 text-purple-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Multi-Role Access</h3>
              <p className="text-sm text-gray-600">Owner, Coordinator, Operators, Client views</p>
            </div>
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <Calculator className="w-8 h-8 text-green-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Billing & Invoices</h3>
              <p className="text-sm text-gray-600">Auto-calculate totals with PDF export</p>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex flex-col space-y-6">
          <Card className="backdrop-blur-sm bg-white/80 border-white/20">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-center">Welcome</CardTitle>
              <p className="text-gray-600 text-center text-sm">Sign in to access your dashboard</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Mobile Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="tel"
                      placeholder="10-digit mobile number"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                      required
                      maxLength={10}
                      className="bg-white/50 pl-10"
                      data-testid="mobile-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-white/50 pr-10"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Demo Accounts</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 max-h-[280px] overflow-y-auto">
                {demoAccounts.map((account) => (
                  <Button
                    key={account.mobile}
                    type="button"
                    variant="outline"
                    onClick={() => quickLogin(account.mobile)}
                    className="w-full justify-between bg-white/50 hover:bg-white/80 text-xs h-auto py-2 px-3"
                    disabled={loading}
                    data-testid={`demo-login-${account.badge.toLowerCase()}`}
                  >
                    <span className="truncate">{account.label}</span>
                    <Badge className={`${account.color} text-[10px] ml-1`}>{account.badge}</Badge>
                  </Button>
                ))}
              </div>

              <div className="text-center">
                <p className="text-xs text-gray-500">
                  All demo accounts use password: <strong>demo123</strong>
                </p>
              </div>

              {/* Clear Session Button - for debugging login issues */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    console.log('[DEBUG] Clearing localStorage');
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('user');
                    toast({
                      title: "Session Cleared",
                      description: "Local storage cleared. Please try logging in again.",
                    });
                    window.location.reload();
                  }}
                  className="text-xs text-gray-400 hover:text-gray-600 underline"
                  data-testid="clear-session-btn"
                >
                  Having login issues? Click to clear session
                </button>
              </div>

              {/* Install App Button */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Button 
                  variant="outline" 
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0 hover:from-green-600 hover:to-emerald-700"
                  onClick={() => setShowInstallGuide(true)}
                  data-testid="install-app-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  📱 Install App on Your Device
                </Button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Works on Android, iPhone & Desktop
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Install App Guide Dialog */}
      <InstallAppGuide 
        open={showInstallGuide} 
        onClose={() => setShowInstallGuide(false)} 
      />
    </div>
  );
};

export default LoginPage;
